import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React from 'react';

import { ActionPermissions, CustomPermission } from '../../../@types';
import { Link } from '../../../components/atoms/Link';
import { isUserHasPermission } from '../../../configs/permissions';
import { STANDARD_FIELD_HEIGHT } from '../../../configs/ui-constants';

export interface StructureListHeaderProps {
  total: number;
  permissions: CustomPermission[] | undefined;
  createHref: string;
  exportAnchor: HTMLElement | null;
  onExportClick: (event: React.MouseEvent<HTMLElement>) => void;
  onExportClose: () => void;
  onExportCsv: () => void;
  onExportExcel: () => void;
  /** Callback to open the column-picker dialog. When omitted, the Columns
   *  button is hidden — used for the client/customer curated view. */
  onColumnsClick?: () => void;
  /** Callback to open the saved-reports modal. */
  onReportsClick: () => void;
  onImportClick?: () => void;
  showImport?: boolean;
}

const TOOLBAR_BUTTON_SX = {
  textTransform: 'none' as const,
  fontSize: '12px',
  color: '#fff',
  borderColor: 'rgba(255, 255, 255, 0.3)',
  bgcolor: 'transparent',
  py: 0.25,
  height: STANDARD_FIELD_HEIGHT,
  minHeight: STANDARD_FIELD_HEIGHT,
  '&:hover': {
    borderColor: '#fff',
    bgcolor: 'rgba(255, 255, 255, 0.08)',
  },
} as const;

export const StructureListHeader: React.FC<StructureListHeaderProps> = ({
  total,
  permissions,
  createHref,
  exportAnchor,
  onExportClick,
  onExportClose,
  onExportCsv,
  onExportExcel,
  onColumnsClick,
  onReportsClick,
  onImportClick,
  showImport,
}) => {
  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'v4.sidebar',
        color: '#fff',
        px: '20px',
        py: '12px',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
          Structures
        </Typography>
        {total > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: '11px',
              color: 'rgba(255, 255, 255, 0.85)',
              fontWeight: 400,
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              px: 1,
              py: '2px',
              borderRadius: '10px',
            }}
          >
            {total.toLocaleString()} results
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {isUserHasPermission(ActionPermissions.Report_My_Report_Search, permissions) && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<SummarizeOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onReportsClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Reports
          </MuiButton>
        )}
        {onColumnsClick && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onColumnsClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Columns
          </MuiButton>
        )}
        {isUserHasPermission(ActionPermissions.Structure_Export, permissions) && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onExportClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Export
          </MuiButton>
        )}
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={onExportClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={onExportCsv}>Export CSV</MenuItem>
          <MenuItem onClick={onExportExcel}>Export Excel</MenuItem>
        </Menu>
        {showImport && onImportClick && (
          <MuiButton
            variant="outlined"
            size="small"
            startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={onImportClick}
            sx={TOOLBAR_BUTTON_SX}
          >
            Import
          </MuiButton>
        )}
        {isUserHasPermission(ActionPermissions.Structure_Create_General, permissions) && (
          <Link href={createHref}>
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              sx={TOOLBAR_BUTTON_SX}
            >
              Create Structure
            </MuiButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};
